import React from 'react';
import { Calendar, Clock } from 'lucide-react';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';

export default function ScheduleTab({ handleSaveSchedule, handleSaveScheduling, loading, scheduleSettings, schedulingSettings, setScheduleSettings, setSchedulingSettings }) {
  return (
        <div className="glass-card">
          <h2 className="text-xl sm:text-2xl font-semibold text-white mb-6 sm:mb-8">Horários de Funcionamento</h2>
          
          {/* Auto Open/Close Toggle - Mobile optimized */}
          <div className="mb-8 p-5 sm:p-6 bg-white/5 rounded-xl border border-white/10">
            <div className="flex items-start sm:items-center justify-between gap-4 mb-4">
              <div className="flex-1">
                <Label htmlFor="schedule-auto" className="text-white font-semibold mb-2 text-base sm:text-lg block cursor-pointer">
                  Abertura/Fechamento Automático
                </Label>
                <p className="text-gray-400 text-sm leading-relaxed">
                  O estabelecimento abrirá e fechará automaticamente nos horários configurados
                </p>
              </div>
              <div className="flex-shrink-0" style={{ minWidth: '48px', minHeight: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Switch
                  id="schedule-auto"
                  checked={scheduleSettings.auto_open_close}
                  onCheckedChange={(checked) => setScheduleSettings({ ...scheduleSettings, auto_open_close: checked })}
                  data-testid="schedule-auto-toggle"
                />
              </div>
            </div>
            {scheduleSettings.auto_open_close && (
              <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                <div className="flex items-center text-blue-400 text-sm">
                  <Clock size={18} className="mr-2 flex-shrink-0" />
                  <span>O sistema controlará automaticamente o status do estabelecimento</span>
                </div>
              </div>
            )}
          </div>

          {/* Lunch Break Toggle - Pausa para Almoço */}
          <div className="mb-8 p-5 sm:p-6 bg-white/5 rounded-xl border border-white/10">
            <div className="flex items-start sm:items-center justify-between gap-4 mb-4">
              <div className="flex-1">
                <Label htmlFor="lunch-break-toggle" className="text-white font-semibold mb-2 text-base sm:text-lg block cursor-pointer">
                  Pausa para Almoço
                </Label>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Habilite se o estabelecimento fecha durante o horário de almoço
                </p>
              </div>
              <div className="flex-shrink-0" style={{ minWidth: '48px', minHeight: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Switch
                  id="lunch-break-toggle"
                  checked={scheduleSettings.lunch_break_enabled}
                  onCheckedChange={(checked) => setScheduleSettings({ ...scheduleSettings, lunch_break_enabled: checked })}
                  data-testid="lunch-break-toggle"
                />
              </div>
            </div>
            
            {scheduleSettings.lunch_break_enabled && (
              <div className="mt-4 space-y-4">
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
                  <div className="flex items-center text-amber-400 text-sm mb-3">
                    <Clock size={18} className="mr-2 flex-shrink-0" />
                    <span>Configure o horário padrão da pausa. Você pode personalizar por dia abaixo.</span>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Início da Pausa</label>
                      <input
                        type="time"
                        className="glass-input w-full text-base sm:text-sm"
                        style={{ minHeight: '48px', padding: '12px 16px' }}
                        value={scheduleSettings.lunch_break_start}
                        onChange={(e) => setScheduleSettings({ ...scheduleSettings, lunch_break_start: e.target.value })}
                        data-testid="lunch-break-start"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Fim da Pausa</label>
                      <input
                        type="time"
                        className="glass-input w-full text-base sm:text-sm"
                        style={{ minHeight: '48px', padding: '12px 16px' }}
                        value={scheduleSettings.lunch_break_end}
                        onChange={(e) => setScheduleSettings({ ...scheduleSettings, lunch_break_end: e.target.value })}
                        data-testid="lunch-break-end"
                      />
                    </div>
                  </div>
                  
                  <button
                    onClick={() => {
                      const newSchedule = { ...scheduleSettings.schedule };
                      Object.keys(newSchedule).forEach(day => {
                        if (newSchedule[day].enabled) {
                          newSchedule[day] = { ...newSchedule[day], lunch_break: true };
                        }
                      });
                      setScheduleSettings({ ...scheduleSettings, schedule: newSchedule });
                    }}
                    className="mt-4 px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded-lg text-sm font-medium transition-all"
                  >
                    Aplicar pausa para todos os dias ativos
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Weekly Schedule - Layout card para mobile */}
          <div className="space-y-4 sm:space-y-5">
            <h3 className="text-lg sm:text-xl font-semibold text-white mb-4">Horários por Dia da Semana</h3>
            
            {Object.entries({
              monday: 'Segunda-feira',
              tuesday: 'Terça-feira',
              wednesday: 'Quarta-feira',
              thursday: 'Quinta-feira',
              friday: 'Sexta-feira',
              saturday: 'Sábado',
              sunday: 'Domingo'
            }).map(([day, label]) => (
              <div 
                key={day} 
                className={`p-5 sm:p-6 rounded-xl border-2 transition-all duration-200 ${
                  scheduleSettings.schedule[day].enabled 
                    ? 'bg-white/5 border-white/10 hover:border-white/20' 
                    : 'bg-gray-800/30 border-gray-800/50'
                }`}
              >
                {/* Header do card - Nome do dia e toggle */}
                <div className="flex items-center justify-between mb-5 sm:mb-4">
                  <div className="flex items-center space-x-4">
                    <div style={{ minWidth: '48px', minHeight: '48px', display: 'flex', alignItems: 'center' }}>
                      <Switch
                        id={`schedule-${day}`}
                        checked={scheduleSettings.schedule[day].enabled}
                        onCheckedChange={(checked) => 
                          setScheduleSettings({
                            ...scheduleSettings,
                            schedule: {
                              ...scheduleSettings.schedule,
                              [day]: { ...scheduleSettings.schedule[day], enabled: checked }
                            }
                          })
                        }
                        data-testid={`schedule-${day}-toggle`}
                      />
                    </div>
                    <Label 
                      htmlFor={`schedule-${day}`}
                      className={`font-semibold text-base sm:text-lg cursor-pointer ${
                        scheduleSettings.schedule[day].enabled ? 'text-white' : 'text-gray-500'
                      }`}
                    >
                      {label}
                    </Label>
                  </div>
                  {!scheduleSettings.schedule[day].enabled && (
                    <span className="text-gray-500 text-sm italic px-3 py-1 bg-gray-800/50 rounded-lg">Fechado</span>
                  )}
                </div>

                {/* Horários - Layout vertical em mobile */}
                {scheduleSettings.schedule[day].enabled && (
                  <div className="space-y-4">
                    <div className="space-y-4 sm:space-y-0 sm:flex sm:items-center sm:space-x-4 pl-0 sm:pl-16">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-400 mb-2">Horário de Abertura</label>
                        <input
                          type="time"
                          className="glass-input w-full text-base sm:text-sm"
                          style={{ minHeight: '48px', padding: '12px 16px' }}
                          value={scheduleSettings.schedule[day].open}
                          onChange={(e) => 
                            setScheduleSettings({
                              ...scheduleSettings,
                              schedule: {
                                ...scheduleSettings.schedule,
                                [day]: { ...scheduleSettings.schedule[day], open: e.target.value }
                              }
                            })
                          }
                          data-testid={`schedule-${day}-open`}
                        />
                      </div>
                      
                      <div className="hidden sm:flex items-center text-gray-500 self-end pb-3">—</div>
                      
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-400 mb-2">Horário de Fechamento</label>
                        <input
                          type="time"
                          className="glass-input w-full text-base sm:text-sm"
                          style={{ minHeight: '48px', padding: '12px 16px' }}
                          value={scheduleSettings.schedule[day].close}
                          onChange={(e) => 
                            setScheduleSettings({
                              ...scheduleSettings,
                              schedule: {
                                ...scheduleSettings.schedule,
                                [day]: { ...scheduleSettings.schedule[day], close: e.target.value }
                              }
                            })
                          }
                          data-testid={`schedule-${day}-close`}
                        />
                      </div>
                    </div>
                    
                    {/* Checkbox de Pausa para Almoço por dia */}
                    {scheduleSettings.lunch_break_enabled && (
                      <div className="pl-0 sm:pl-16 mt-3">
                        <label className="flex items-center space-x-3 cursor-pointer p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg hover:bg-amber-500/15 transition-colors">
                          <input
                            type="checkbox"
                            checked={scheduleSettings.schedule[day].lunch_break || false}
                            onChange={(e) => 
                              setScheduleSettings({
                                ...scheduleSettings,
                                schedule: {
                                  ...scheduleSettings.schedule,
                                  [day]: { ...scheduleSettings.schedule[day], lunch_break: e.target.checked }
                                }
                              })
                            }
                            className="w-5 h-5 rounded border-amber-500/50 text-amber-500 focus:ring-amber-500/50 bg-transparent"
                            data-testid={`schedule-${day}-lunch-break`}
                          />
                          <span className="text-sm text-amber-400">
                            Pausa para almoço ({scheduleSettings.lunch_break_start} - {scheduleSettings.lunch_break_end})
                          </span>
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Quick Actions - Mobile friendly */}
          <div className="mt-8 p-5 sm:p-6 bg-white/5 rounded-xl border border-white/10">
            <h4 className="text-white font-semibold mb-4 text-base">Ações Rápidas</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                onClick={() => {
                  const newSchedule = { ...scheduleSettings.schedule };
                  Object.keys(newSchedule).forEach(day => {
                    newSchedule[day] = { enabled: true, open: '10:00', close: '22:00' };
                  });
                  setScheduleSettings({ ...scheduleSettings, schedule: newSchedule });
                }}
                className="px-4 py-3 bg-white/10 hover:bg-white/20 active:bg-white/30 text-white rounded-xl text-sm font-medium transition-all duration-200 active:scale-95"
                style={{ minHeight: '48px' }}
              >
                Aplicar 10h-22h para todos
              </button>
              
              <button
                onClick={() => {
                  const newSchedule = { ...scheduleSettings.schedule };
                  ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].forEach(day => {
                    newSchedule[day] = { enabled: true, open: '10:00', close: '22:00' };
                  });
                  newSchedule.saturday = { enabled: true, open: '10:00', close: '23:00' };
                  newSchedule.sunday = { enabled: true, open: '11:00', close: '21:00' };
                  setScheduleSettings({ ...scheduleSettings, schedule: newSchedule });
                }}
                className="px-4 py-3 bg-white/10 hover:bg-white/20 active:bg-white/30 text-white rounded-xl text-sm font-medium transition-all duration-200 active:scale-95"
                style={{ minHeight: '48px' }}
              >
                Horário comercial padrão
              </button>

              <button
                onClick={() => {
                  const newSchedule = { ...scheduleSettings.schedule };
                  Object.keys(newSchedule).forEach(day => {
                    newSchedule[day] = { enabled: false, open: '10:00', close: '22:00' };
                  });
                  setScheduleSettings({ ...scheduleSettings, schedule: newSchedule });
                }}
                className="px-4 py-3 bg-red-500/20 hover:bg-red-500/30 active:bg-red-500/40 text-red-400 rounded-xl text-sm font-medium transition-all duration-200 active:scale-95"
                style={{ minHeight: '48px' }}
              >
                Desativar todos
              </button>
            </div>
          </div>

          <div className="flex justify-end mt-8 pt-6 border-t border-white/10">
            <button
              onClick={handleSaveSchedule}
              disabled={loading}
              className="glass-button-primary w-full sm:w-auto"
              style={{ minHeight: '48px', padding: '12px 32px', fontSize: '16px' }}
              data-testid="schedule-save-button"
            >
              {loading ? 'Salvando...' : 'Salvar Horários'}
            </button>
          </div>

          {/* Scheduling Section - Agendamento de Pedidos */}
          <div className="mt-10 pt-8 border-t border-white/10">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Calendar className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Agendamento de Pedidos</h3>
                <p className="text-sm text-gray-400">Permita que clientes agendem pedidos para horários futuros</p>
              </div>
            </div>

            {/* Enable scheduling when closed */}
            <div className="space-y-4">
              <div className="p-5 bg-white/5 rounded-xl border border-white/10">
                <div className="flex items-start sm:items-center justify-between gap-4 mb-3">
                  <div className="flex-1">
                    <Label htmlFor="scheduling-enabled" className="text-white font-semibold text-base block cursor-pointer">
                      Permitir agendamento quando fechado
                    </Label>
                    <p className="text-gray-400 text-sm mt-1">
                      Clientes podem agendar pedidos mesmo quando a loja está fechada
                    </p>
                  </div>
                  <div className="flex-shrink-0" style={{ minWidth: '48px', minHeight: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Switch
                      id="scheduling-enabled"
                      checked={schedulingSettings.scheduling_enabled}
                      onCheckedChange={(checked) => setSchedulingSettings({ ...schedulingSettings, scheduling_enabled: checked })}
                      data-testid="scheduling-enabled-toggle"
                    />
                  </div>
                </div>
              </div>

              {/* Enable scheduling when open */}
              {schedulingSettings.scheduling_enabled && (
                <div className="p-5 bg-white/5 rounded-xl border border-white/10">
                  <div className="flex items-start sm:items-center justify-between gap-4 mb-3">
                    <div className="flex-1">
                      <Label htmlFor="scheduling-when-open" className="text-white font-semibold text-base block cursor-pointer">
                        Permitir agendamento quando aberto
                      </Label>
                      <p className="text-gray-400 text-sm mt-1">
                        Clientes também podem agendar pedidos enquanto a loja está aberta
                      </p>
                    </div>
                    <div className="flex-shrink-0" style={{ minWidth: '48px', minHeight: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Switch
                        id="scheduling-when-open"
                        checked={schedulingSettings.scheduling_when_open}
                        onCheckedChange={(checked) => setSchedulingSettings({ ...schedulingSettings, scheduling_when_open: checked })}
                        data-testid="scheduling-when-open-toggle"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Max days for scheduling */}
              {schedulingSettings.scheduling_enabled && (
                <div className="p-5 bg-white/5 rounded-xl border border-white/10">
                  <Label htmlFor="scheduling-max-days" className="text-white font-semibold text-base block mb-3">
                    Máximo de dias para agendamento
                  </Label>
                  <p className="text-gray-400 text-sm mb-4">
                    Quantos dias no futuro o cliente pode agendar
                  </p>
                  <select
                    id="scheduling-max-days"
                    className="glass-input w-full text-base"
                    style={{ minHeight: '48px', padding: '12px 16px' }}
                    value={schedulingSettings.scheduling_max_days}
                    onChange={(e) => setSchedulingSettings({ ...schedulingSettings, scheduling_max_days: parseInt(e.target.value) })}
                    data-testid="scheduling-max-days-select"
                  >
                    <option value={3}>3 dias</option>
                    <option value={5}>5 dias</option>
                    <option value={7}>7 dias</option>
                    <option value={14}>14 dias</option>
                    <option value={30}>30 dias</option>
                  </select>
                </div>
              )}

              {/* Info box */}
              {schedulingSettings.scheduling_enabled && (
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-300">
                      <p className="font-medium mb-1">Como funciona:</p>
                      <ul className="list-disc list-inside space-y-1 text-blue-300/80">
                        <li>Quando a loja estiver fechada, o cliente verá opção de agendar</li>
                        <li>Os horários disponíveis seguem o funcionamento configurado acima</li>
                        <li>Os pedidos agendados aparecem com destaque no painel de pedidos</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={handleSaveScheduling}
                disabled={loading}
                className="glass-button-primary w-full sm:w-auto"
                style={{ minHeight: '48px', padding: '12px 32px', fontSize: '16px' }}
                data-testid="scheduling-save-button"
              >
                {loading ? 'Salvando...' : 'Salvar Agendamento'}
              </button>
            </div>
          </div>
        </div>
  );
}
